// Container wrapper with a text header
import { StackLayout } from "@progress/kendo-react-layout";
import { Box, CPText, Container } from "shared/components/base";
import * as React from "react";
import "./CPPanelStyles.scss";

interface CPPanelProps {
  /** Optional title text displayed in the panel header bar. When omitted, no header is rendered. */
  header?: string;
  /** Content rendered in the panel body. */
  children?: React.ReactNode;
  /** Inline styles applied to the outer container. */
  style?: React.CSSProperties;
  /** Padding of the panel body — number (px) or a CSS string. */
  padding?: number | string;
  /** Action elements (e.g. icon buttons) rendered on the right side of the header. */
  actionBtns?: React.ReactNode;
}

/**
 * Bordered container with an optional header bar and action-button slot.
 * Use it to group related form fields or content sections.
 *
 * @example
 * ```tsx
 * // Simple panel with a header
 * <CPPanel header="Contact Details">
 *   <CPTextBox name="phone" label="Phone" value={form.phone} onChange={handleChange} />
 * </CPPanel>
 *
 * // Panel with action buttons in the header
 * <CPPanel
 *   header="Addresses"
 *   actionBtns={<CPButton onClick={handleAdd} icon="plus">Add</CPButton>}
 * >
 *   <AddressList addresses={addresses} />
 * </CPPanel>
 * ```
 */
const CPPanel: React.FC<CPPanelProps> = (props) => {
  const renderHeader = () => {
    if (props.header == null) return null;

    return (
      <Box className="cp-panel-header">
        <CPText style={{ fontSize: ".9em" }}>{props.header}</CPText>
        <Box
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          spacing={5}
        >
          {props.actionBtns}
        </Box>
      </Box>
    );
  };

  return (
    <Container className="cp-panel-container" style={props.style} padding={0}>
      {renderHeader()}
      <Box className="cp-panel-content">{props.children}</Box>
    </Container>
  );
};

export default CPPanel;
